import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { useDispatch, useSelector } from 'react-redux';
import MessageBox from 'yoda-core-components/lib/components/MessageBox/MessageBox';
import {
    selectDeviceType,
    selectFeatureFlags,
    selectContext,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { filter, find, forEach, isEmpty } from 'lodash';
import {
    triggerFormError,
    triggerModuleImpressionTracking,
} from 'yoda-site-components/lib/actions/AnalyticsAction';
import {
    ADJUSTMENT_TYPE_COUPON,
    BNPL_APPLIED_ON_CHECKOUT,
    COUPON_MESSAGES_SPEND_MORE_TO_QUALIFY,
} from 'yoda-checkout-components/lib/common/Constants';
import ApplePayErrorMessage from 'yoda-checkout-components/lib/components/ApplePayCTA/ApplePayErrorMessage/ApplePayErrorMessage';
import ServiceErrorMessage from 'yoda-checkout-components/lib/components/ServiceErrorMessage/ServiceErrorMessage';
import BopisHolidayMessage from 'yoda-checkout-components/lib/components/BopisHolidayMessage/BopisHolidayMessage';
import { scrollToElement } from 'yoda-checkout-components/lib/helpers/Utils/ATCCommonUtils';
import { selectBnplOnOrder } from 'yoda-checkout-components/lib/selectors/PaymentMethodSelector';
import BopisExclusionWarning from 'yoda-checkout-components/lib/components/BopisExclusionWarning/BopisExclusionWarning';
import { isAgentLogged } from 'yoda-core-components/lib/helpers/Utils/Utils';
import SavedItemAddToCartMessageConnect from '../SavedItemsProductCartridge/SavedItemAddToCartMessage';
import {
    default as OOSDiscontinuedMessage,
    default as B2BAgentMessage,
} from '../OOSDiscontinuedMessage';
import GiftCardBannerMsgBox from '../GiftCardBanner/GiftCardBannerMessage';
import { deliveryChangedLabels, giftCardRegularItemErrorCodes } from '../../common/LabelConstants';
import InvalidCouponInfoMessage from './InvalidCouponInfoMessage';
import BXGXPromotionBannerLazy from '../BXGXPromotionBanner/BXGXPromotionBannerLazy';
import MPRestrictedPaymentMessageLazy from '../MPRestrictedPaymentMessage/MPRestrictedPaymentMessageLazy';
import PaypalIncompatibleBasketMessageLazy from '../PaypalIncompatibleBasketMessage/PaypalIncompatibleBasketMessageLazy';

function CartMessages({
    isTotalExceededError,
    cartTotalExceededErrorMsg,
    isNotAGiftCardOrder,
    bxgxPromotionMessages,
}) {
    const serviceErrors = useSelector((state) => state.serviceErrors);
    const {
        enableRewardRedemption = true,
        enableFulfillmentOptionMessaging = true,
        enableMarketPlace = false,
        enableMPRestrictedPaymentMessage = true,
    } = useSelector(selectFeatureFlags);
    const orderAdjustments = useSelector((state) => state.orderAdjustments) || [];
    const orderInfo = useSelector((state) => state.orderInfo) || {};
    const marketPlaceItemPresent = useSelector(
        (state) => state.cartDetails?.data?.marketPlaceItemPresent
    );
    const isBnplOnOrder = useSelector(selectBnplOnOrder);
    const dispatch = useDispatch();
    const [isBnplApplied, setBnplApplied] = useState(false);
    const context = useSelector(selectContext);
    const isBnplAppliedErrorMsg =
        useSelector((state) => state.context?.preferences?.bnplConfig?.isBnplAppliedErrorMsg) ||
        BNPL_APPLIED_ON_CHECKOUT;
    const totalExceededError = useRef(null);
    const fulFillmentMessageBlock = useRef(null);
    const giftCardError = useRef(null);
    const isGiftCardPaymentErrAnalyticsTriggered = useRef(false);
    const isGiftCardRegularItemErrAnalyticsTriggered = useRef(false);
    const showGlobalLoader = useSelector((state) => state.showGlobalLoader);
    const showGCMessageBox = useSelector((state) => state.giftCardBannerData?.showGCMsgBanner);
    const deviceType = useSelector(selectDeviceType);
    const { isDesktop } = deviceType;
    const hasMarketPlaceItems =
        enableMarketPlace && enableMPRestrictedPaymentMessage && Boolean(marketPlaceItemPresent);

    /* istanbul ignore next */
    let isRewardExpiredError = false;
    let isRewardExpiredErrorMsg = '';
    const cartRewardsErrorCode = 'API_ERR_REDEEMED_COUPON_PRESENT';
    let cartRewardsErrorExceptions = {};
    if (enableRewardRedemption && orderInfo?.status?.exceptions) {
        /* istanbul ignore next */
        cartRewardsErrorExceptions = find(orderInfo.status.exceptions, {
            errorCode: cartRewardsErrorCode,
        });
        if (cartRewardsErrorExceptions) {
            isRewardExpiredError = true;
            isRewardExpiredErrorMsg = cartRewardsErrorExceptions.errorMessage;
        }
    }

    useEffect(() => {
        if (isBnplOnOrder) {
            dispatch(
                triggerFormError([
                    {
                        errorDescription: isBnplAppliedErrorMsg,
                    },
                ])
            );
            setBnplApplied(true);
        } else {
            setBnplApplied(false);
        }
    }, [isBnplOnOrder]);

    useEffect(() => {
        if (totalExceededError.current && !showGlobalLoader) {
            scrollToElement(totalExceededError.current);
        }
    }, [totalExceededError.current]);

    /* istanbul ignore next */
    const scrollToItem = (cartItemId) => {
        const match = document.getElementById(cartItemId);
        if (match && window) {
            const rect = match.getBoundingClientRect();
            const anchorOffset = window.pageYOffset + rect.top - 55;
            window.scrollTo(0, anchorOffset);
        }
    };

    const renderAdjustmentSpendMoreMessage = (orderAdjustmentsList) => {
        const msgPattern = new RegExp(COUPON_MESSAGES_SPEND_MORE_TO_QUALIFY);
        const matchedAdj = filter(
            orderAdjustmentsList,
            (adj) => adj.code === ADJUSTMENT_TYPE_COUPON && msgPattern.test(adj.message)
        );
        return matchedAdj.length > 0 ? (
            <div>
                <MessageBox
                    type="information"
                    level="section"
                    title={matchedAdj[0].message}
                    className={dt(['mt-4'])}
                    automationId="at-cart-spend-more-warning"
                    hasClose={false}
                />
            </div>
        ) : (
            ''
        );
    };

    const renderDeliveryChangedItems = (items) => {
        scrollToElement(fulFillmentMessageBlock.current);
        const itemsData = items.map((item) => (
            <li className={dt(['mt-1', 'list-disc', 'underline', 'ml-3'])}>
                <button
                    type="button"
                    onClick={() => scrollToItem(item.id)}
                    className={dt(['text-black', 'underline', 'text-left'])}
                >
                    {item?.product?.name || ''}
                </button>
            </li>
        ));
        const itemCount = items.length;
        const deliveryMethodChangedMessage =
            itemCount > 1
                ? `${itemCount} ${deliveryChangedLabels.DELIVERY_METHODS_CHANGED_MULTIPLE}`
                : `${itemCount} ${deliveryChangedLabels.DELIVERY_METHODS_CHANGED_SINGLE}`;
        return (
            <div
                className={dt(['font-open-sans-regular', 'text-normal', 'mt-1'])}
                data-automation-id="at-delivery-changed-items-block"
            >
                {deliveryMethodChangedMessage}
                <ul className={dt(['ml-3'])}>{itemsData}</ul>
            </div>
        );
    };

    /**
     * Fetch the giftCard and Merchadise error from the draft order status response
     */
    const getGiftCardRegularMixItemError = () => {
        let errorContainer = [];
        const exceptions = orderInfo?.status?.exceptions;
        if (!isEmpty(exceptions)) {
            errorContainer = filter(
                exceptions,
                (error) =>
                    error.errorCode ===
                        giftCardRegularItemErrorCodes.SRV_BAG_GIFT_CARD_WITH_GIFT_CARD ||
                    error.errorCode ===
                        giftCardRegularItemErrorCodes.SVC_ORD_ERR_MERCHANDISE_NOTALLOWED
            );
        }
        return errorContainer;
    };

    /**
     * Return gift card regular item error component
     */
    const getGiftCardRegularMixItemErrorComponent = () => {
        const errors = getGiftCardRegularMixItemError();
        const errorComponent = [];
        forEach(errors, (error) => {
            /* istanbul ignore else */
            if (error.errorMessage) {
                const isGiftCardPaymentError =
                    error.errorCode ===
                    giftCardRegularItemErrorCodes.SRV_BAG_GIFT_CARD_WITH_GIFT_CARD;
                errorComponent.push(
                    <div
                        ref={(input) => {
                            isGiftCardPaymentError && (giftCardError.current = input);
                        }}
                        className={dt(['py-0', 'mb-6', 'mt-4'])}
                    >
                        <MessageBox
                            type="information"
                            level="section"
                            title={error.errorMessage}
                            hasClose={false}
                            automationId={`at-${error.errorCode}`}
                        />
                    </div>
                );
            }
        });
        return errorComponent;
    };

    /**
     * Trigger analytics event to capture giftCard and merchadise error
     */
    const triggerGiftCardRegularItemErrorToAnalytics = () => {
        const errors = getGiftCardRegularMixItemError();
        forEach(errors, (error) => {
            if (
                error.errorCode ===
                    giftCardRegularItemErrorCodes.SRV_BAG_GIFT_CARD_WITH_GIFT_CARD &&
                !isGiftCardPaymentErrAnalyticsTriggered.current
            ) {
                /* istanbul ignore else */
                if (giftCardError.current) {
                    scrollToElement(giftCardError.current);
                }
                dispatch(
                    triggerFormError([
                        {
                            errorDescription: error.errorMessage,
                        },
                    ])
                );
                isGiftCardPaymentErrAnalyticsTriggered.current = true;
            } else if (
                error.errorCode ===
                    giftCardRegularItemErrorCodes.SVC_ORD_ERR_MERCHANDISE_NOTALLOWED &&
                !isGiftCardRegularItemErrAnalyticsTriggered.current
            ) {
                dispatch(
                    triggerFormError([
                        {
                            errorDescription: error.errorMessage,
                        },
                    ])
                );
                isGiftCardRegularItemErrAnalyticsTriggered.current = true;
            }
        });
    };

    useEffect(() => {
        triggerGiftCardRegularItemErrorToAnalytics();
    });

    useEffect(() => {
        if (!bxgxPromotionMessages || bxgxPromotionMessages.length === 0) {
            dispatch(
                triggerModuleImpressionTracking({
                    name: 'cart : bmgm: promotion banner not displayed',
                    linktext: 'Promo banner not displyed',
                    locationpath: '/cart',
                    targetpath: '',
                    linkposition: '0',
                })
            );
            return;
        }
        bxgxPromotionMessages.forEach((promo) => {
            dispatch(
                triggerModuleImpressionTracking({
                    name: `cart : bmgm: ${promo.mktLabel}`,
                    linktext: 'Promo savings available message displayed',
                    locationpath: '/cart',
                    targetpath: '',
                    linkposition: '0',
                })
            );
        });
    }, [bxgxPromotionMessages]);

    return (
        <>
            {
                /* istanbul ignore next */ isTotalExceededError &&
                !(serviceErrors?.errors?.errorCode === 'SRV_TOTAL_EXCEEDED_ERROR') ? (
                    <div className={dt(['py-0', 'mt-2', 'mb-6'])} ref={totalExceededError}>
                        <MessageBox
                            type="error"
                            level="section"
                            title={cartTotalExceededErrorMsg}
                            hasClose={false}
                            automationId="at-total-exceeded-error"
                        />
                    </div>
                ) : null
            }
            <BopisExclusionWarning fromPage="cart" />
            {
                /* istanbul ignore next */
                isRewardExpiredError ? (
                    <div className={dt(['py-0', 'mt-2', 'mb-6'])} ref={totalExceededError}>
                        <MessageBox
                            type="warning"
                            level="section"
                            title={isRewardExpiredErrorMsg}
                            hasClose={false}
                            automationId="at-total-exceeded-error"
                        />
                    </div>
                ) : null
            }
            {
                /* istanbul ignore next */
                isBnplApplied ? (
                    <div className={dt(['py-0', 'mt-2', 'mb-6'])} ref={totalExceededError}>
                        <MessageBox
                            type="warning"
                            level="section"
                            title={isBnplAppliedErrorMsg}
                            hasClose
                            onClose={() => setBnplApplied(false)}
                            automationId="at-bnpl-applied-error"
                        />
                    </div>
                ) : null
            }
            {getGiftCardRegularMixItemErrorComponent()}
            <SavedItemAddToCartMessageConnect />
            <ApplePayErrorMessage />
            <div>
                <ServiceErrorMessage
                    contextList={['CART']}
                    componentName="CART_SECTION"
                    srvErrorWrapperClass={['!mt-4']}
                />
            </div>
            <BopisHolidayMessage source="cart" />
            <OOSDiscontinuedMessage fromOOS />
            {isAgentLogged(context) ? <B2BAgentMessage fromB2bAgentMarketPlace /> : <></>}
            {showGCMessageBox && (
                <div className={dt(['lg:hidden'])}>
                    <GiftCardBannerMsgBox />
                </div>
            )}
            {enableFulfillmentOptionMessaging && !isEmpty(orderInfo.deliveryChangedItemsData) && (
                <div className={dt(['mb-6', 'mt-4'])} ref={fulFillmentMessageBlock}>
                    <MessageBox
                        level="section"
                        hasClose={false}
                        theme={dt(['smMd:mb-0'])}
                        automationId="at-delivery-items-changed"
                    >
                        <p className="font-open-sans-semibold">
                            {deliveryChangedLabels.MESSAGE_ABOUT_CART}
                        </p>
                        {renderDeliveryChangedItems(orderInfo.deliveryChangedItemsData)}
                    </MessageBox>
                </div>
            )}
            {isDesktop && <InvalidCouponInfoMessage />}
            {isNotAGiftCardOrder && (
                <div data-automation-id="at-coupons-rewards-row-block">
                    {renderAdjustmentSpendMoreMessage(orderAdjustments)}
                </div>
            )}
            {bxgxPromotionMessages?.length > 0 && <BXGXPromotionBannerLazy />}
            {hasMarketPlaceItems && <MPRestrictedPaymentMessageLazy />}
            <PaypalIncompatibleBasketMessageLazy />
        </>
    );
}

CartMessages.propTypes = {
    isTotalExceededError: PropTypes.bool,
    cartTotalExceededErrorMsg: PropTypes.string,
    isNotAGiftCardOrder: PropTypes.bool,
    bxgxPromotionMessages: PropTypes.arrayOf(
        PropTypes.shape({
            mktLabel: PropTypes.string,
        })
    ),
};

CartMessages.defaultProps = {
    isTotalExceededError: false,
    cartTotalExceededErrorMsg: '',
    isNotAGiftCardOrder: true,
    bxgxPromotionMessages: [],
};

export default CartMessages;
